import { Mail, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@shared/components/ui/shadcn/switch';
import type { NotificationPreference } from '../../models/Notification.types';

export interface NotificationPreferenceState {
  email: boolean;
  inApp: boolean;
}

interface Props {
  preferences: NotificationPreference[];
  values: Record<number, NotificationPreferenceState>;
  disabled?: boolean;
  onToggle: (preference: NotificationPreference, channel: 'inApp' | 'email') => void;
}

export function NotificationPreferenceFields({ preferences, values, disabled = false, onToggle }: Props) {
  const { t } = useTranslation('notificationsModule');
  return (
    <div className="relative isolate rounded-lg border border-border bg-background">
      <div className="sticky top-0 z-10 grid grid-cols-[1fr_4.5rem_4.5rem] items-center gap-3 rounded-t-lg border-b border-border bg-muted px-4 py-3 sm:grid-cols-[1fr_5rem_5rem]">
        <span className="text-sm font-medium">{t('notifications.preferences.notifyAbout')}</span>
        <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <Mail className="h-3.5 w-3.5" aria-hidden />
          {t('notifications.preferences.email')}
        </div>
        <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <Smartphone className="h-3.5 w-3.5" aria-hidden />
          {t('notifications.preferences.inApp')}
        </div>
      </div>
      {preferences.map((preference, index) => {
        const title =
          preference.notification_type?.name ||
          preference.notification_type?.in_app_title ||
          `Type ${preference.notification_type_id}`;
        return (
          <div
            key={preference.id}
            className={`grid grid-cols-[1fr_4.5rem_4.5rem] items-center gap-3 px-4 py-4 sm:grid-cols-[1fr_5rem_5rem] ${index < preferences.length - 1 ? 'border-b border-border' : ''}`}
          >
            <div className="min-w-0 pr-2">
              <p className="text-sm font-medium text-foreground">{title}</p>
              {preference.notification_type?.description && (
                <p className="mt-0.5 text-sm text-muted-foreground">{preference.notification_type?.description}</p>
              )}
            </div>
            <div className="flex justify-center">
              <Switch
                checked={values[preference.id]?.email ?? false}
                disabled={disabled}
                onCheckedChange={() => onToggle(preference, 'email')}
                aria-label={`${title} - ${t('notifications.preferences.email')}`}
                data-testid={`notification-preference-${preference.id}-email`}
              />
            </div>
            <div className="flex justify-center">
              <Switch
                checked={values[preference.id]?.inApp ?? false}
                disabled={disabled}
                onCheckedChange={() => onToggle(preference, 'inApp')}
                aria-label={`${title} - ${t('notifications.preferences.inApp')}`}
                data-testid={`notification-preference-${preference.id}-in-app`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
